import { Platform } from "react-native"
import { Buffer } from "buffer"

import * as Crypto from "expo-crypto"
import * as SecureStore from "expo-secure-store"
import { MMKV } from "react-native-mmkv"

import { ErrorType, reportCrash } from "src/utils/crashReporting"

/**
 * Name under which the MMKV encryption key is stored in the device's secure
 * credential storage (Android Keystore / iOS Keychain) via expo-secure-store.
 */
const MMKV_ENCRYPTION_KEY_NAME = "sidekick.mmkv.encryptionKey"

/**
 * Unencrypted MMKV instance that records which storage migrations
 * have run since app installation.
 */
const MIGRATION_MMKV_INSTANCE_ID = "sidekick.migrations"

/**
 * MMKV migration flags
 */
const MIGRATION_ENCRYPT_GENERATE_KEY = "sidekick.mmkv.migration.encrypt.generateKey"
const MIGRATION_ENCRYPT_INSTANCE_DEFAULT = "sidekick.mmkv.migration.encrypt.instance.default"
const MIGRATION_ENCRYPT_INSTANCE_TAB_SCOPED = "sidekick.mmkv.migration.encrypt.instance.tab-scoped"

const isWeb = Platform.OS === "web"


// App-wide migration metadata storage.
let migrationStore: MMKV | null = null
if (!isWeb) {
  try {
    migrationStore = new MMKV({ id: MIGRATION_MMKV_INSTANCE_ID })
  } catch (e) {
    // MMKV unavailable. Report it.
    reportCrash(
      new Error(
        `[mmkv] migration store unavailable; will regenerate keys: ${e instanceof Error ? e.message : String(e)}`,
      ),
      ErrorType.HANDLED,
    )
  }
}

/**
 * Resolves the persistent, device-bound encryption key for MMKV, regenerating
 * it when the SecureStore entry is present, but the encryption migration hasn't run.
 * (as happens after an iOS uninstall/reinstall, because SecureStore/Keychain entries
 * survive uninstall while the app sandbox does not).
 *
 * FIXME(mmkv-v4): MMKV v3 cannot take a full-strength 128-bit AES-128 key. The key
 * crosses into native as a `string` encoded via UTF-8 (no byte/ArrayBuffer channel),
 * and UTF-8 can't carry bytes 0x80-0xFF as single bytes — so the 16 used key bytes
 * are confined to a known subset. base64 gives ~96 useful bits, ASCII-only would cap
 * at ~112, and 128 is unreachable. This is a binding
 * limit, not an MMKV-core limit: AESCrypt itself accepts arbitrary 16 raw bytes.
 *
 * FIXME(expo-upgrade): once we're on Expo SDK 55+ (expo-crypto >= 55), generate the
 * key with the dedicated AES key API (`AESEncryptionKey.generate()`)
 * instead of hand-rolling random bytes.
 * See https://docs.expo.dev/versions/latest/sdk/crypto/#aes-encryption-and-decryption
 *
 * FIXME(mmkv-v4): AES-256 (`encryptionType: 'AES-256'`) needs react-native-mmkv v4
 * which is a breaking upgrade.
 * On upgrade, switch to AES-256, use a full 32-byte key (the 16-byte truncation above
 * no longer applies), and re-author the webStorage patch
 * (patches/react-native-mmkv+3.3.3.patch) for v4.
 */
function resolveEncryptionKey(): string {
  const existing = SecureStore.getItem(MMKV_ENCRYPTION_KEY_NAME)
  const migrated = migrationStore?.getBoolean(MIGRATION_ENCRYPT_GENERATE_KEY) === true
  if (existing && migrated) return existing

  /**
   * We use `getRandomValues` rather than `getRandomBytes`: the latter falls back to
   * `Math.random` during development (to play nice with the React Native Debugger),
   * which is NOT cryptographically secure. `getRandomValues` is always CSPRNG-backed.
   */
  const key = Buffer.from(Crypto.getRandomValues(new Uint8Array(16))).toString("base64")
  SecureStore.setItem(MMKV_ENCRYPTION_KEY_NAME, key)
  migrationStore?.set(MIGRATION_ENCRYPT_GENERATE_KEY, true)
  return key
}

// Native encryption key, kept in the OS keystore. Only `undefined` if the keystore
// itself is unavailable.
let encryptionKey: string | undefined
if (!isWeb) {
  try {
    encryptionKey = resolveEncryptionKey()
  } catch (e) {
    // Keystore unavailable — we cannot derive/persist an encryption key. Degrade to an unencrypted
    // store so the app still launches. Shouldn't happen. If it happens sensitive data will be
    // stored unencrypted, so report it.
    encryptionKey = undefined
    reportCrash(
      new Error(
        `[mmkv] keystore unavailable; storage will be UNENCRYPTED: ${e instanceof Error ? e.message : String(e)}`,
      ),
      ErrorType.HANDLED,
    )
  }
}

/**
 * Opens an encrypted MMKV instance. Encrypts the existing (previously
 * unencrypted) store in place on first run via `recrypt()` — so existing data
 * (auth tokens, accepted-terms flags, cached conversations, prefs) are preserved.
 *
 * If anything throws (recrypt fails, corrupted/unreadable store, key mismatch),
 * we reset and re-encrypt so the app still launches.
 *
 * @param id           the existing instance id
 * @param migrationFlagKey migration key marking whether this instance is encrypted yet
 */
function openEncrypted(id: string, migrationFlagKey: string): MMKV {
  // Keystore unavailable (no key): degrade to an unencrypted store, but DON'T record a
  // migration state — so if the keystore recovers on a later launch we still encrypt,
  // rather than opening a plaintext store with a key (which would discard its data).
  // (If a store was already encrypted and the keystore later goes missing, opening it
  // without the key discards its data — unavoidable and near-never, same as before.)
  if (!encryptionKey) {
    reportCrash(
      new Error(
        `[mmkv]openEncrypted: keystore encryption key unavailable. Using storage unencrypted`,
      ),
      ErrorType.HANDLED,
    )
    return new MMKV({ id })
  }
  const key = encryptionKey

  try {
    if (migrationStore?.getBoolean(migrationFlagKey) === true) {
      return new MMKV({ id, encryptionKey: key })
    }

    // First launch on the encrypting build (flag absent): open the existing (plaintext) store and
    // encrypt it in place, preserving existing data.
    const store = new MMKV({ id })
    store.recrypt(key)
    migrationStore?.set(migrationFlagKey, true)
    return store
  } catch (e) {
    // Encryption/open failed (recrypt error, corrupt/unreadable store, key mismatch). Reset and
    // re-encrypt the SAME store so the app launches; report it since it's a silent data reset.
    try {
      reportCrash(
        new Error(
          `[mmkv]openEncrypted: encryption failed (${migrationFlagKey}); resetting store: ${e instanceof Error ? e.message : String(e)}`,
        ),
        ErrorType.HANDLED,
      )
    } catch {}
    return resetEncrypted(id, migrationFlagKey, key)
  }
}

/**
 * Last-resort recovery: clears the store at `id` and encrypts it in place.
 *
 * If even the re-encryption fails we must NOT throw — this runs at module load, so a
 * throw would crash the app on launch. Instead we degrade to the (empty, unencrypted)
 * store and report it, and we only record the migration flag once encryption actually
 * succeeds so a later launch retries rather than opening unencrypted data with the key.
 */
function resetEncrypted(id: string, migrationFlagKey: string, key: string): MMKV {
  // Returns the cached instance for `id` if one was already opened this session.
  const store = new MMKV({ id })
  store.clearAll()
  try {
    store.recrypt(key)
    migrationStore?.set(migrationFlagKey, true)
  } catch (e) {
    try {
      reportCrash(
        new Error(
          `[mmkv]resetEncrypted: re-encryption failed (${migrationFlagKey}); storage will be UNENCRYPTED: ${e instanceof Error ? e.message : String(e)}`,
        ),
        ErrorType.HANDLED,
      )
    } catch {}
  }
  return store
}

/**
 * Default app-wide MMKV instance (id `mmkv.default`).
 *
 * Web: backed by `localStorage` → shared across tabs of the same origin (no
 * keystore on web, so it remains unencrypted; the browser sandbox governs it).
 * Native: encrypted at rest with a Keystore/Keychain-backed key; existing data
 * is encrypted in place on upgrade (see openEncrypted).
 *
 * Use this for data that is per-USER (Cognito tokens, audio prefs,
 * accepted-terms flags, env config).
 */
const storage = isWeb
  ? new MMKV()
  : openEncrypted("mmkv.default", MIGRATION_ENCRYPT_INSTANCE_DEFAULT)

/**
 * Per-tab MMKV instance for the exclusive-sessions model (id `tab-scoped`).
 *
 * Web: thanks to the `react-native-mmkv` patch (see
 * `patches/react-native-mmkv+3.3.3.patch`), passing `webStorage: "session"`
 * routes this instance to `sessionStorage` instead of `localStorage`, making
 * the contents per-TAB. The `webStorage` field is a Sidekick patch addition
 * (web-only); native ignores it.
 *
 * Native: a second on-disk instance, also encrypted at rest and encrypted in
 * place on upgrade. Its distinct id keeps native key-prefixing separate from
 * the default instance.
 *
 * Use this for data that is per-TAB:
 *   * `apiHost` / `sponsorId` — different tabs can be in different sponsors
 *   * `userSelectedSession` (role) — different tabs can be in different roles
 *   * conversation cache (`chat.*`) — each tab has its own conversation
 *   * tanstack-query persister cache — caches role/sponsor-scoped queries
 *
 * The `context_token` itself does NOT live here — it has its own dedicated
 * `sessionStorage` access in `src/utils/contextToken.ts`.
 */
const sessionScopedStorage = isWeb
  ? new MMKV({ id: "tab-scoped", webStorage: "session" })
  : openEncrypted("tab-scoped", MIGRATION_ENCRYPT_INSTANCE_TAB_SCOPED)

export { storage, sessionScopedStorage, migrationStore }
